import { Injectable } from '@angular/core';
import { IdssAnsGoApi, SimuladorGoApi } from '@gestor-web/@core/api/go';
import { Operadora, VinculoUsuarioOperadora } from '@gestor-web/@core/api/ords/model';
import { OperadorasActions, UserProfileActions } from '@gestor-web/@core/store/actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';import { concatLatestFrom } from '@ngrx/operators';

import { Store } from '@ngrx/store';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, skipWhile, switchMap } from 'rxjs/operators';
import { OperadorasSelectors, UserProfileSelectors } from '../selectors';
import { GeralService } from '@gestor-web/app/shared/services';
import { resolverAbrangencia } from '../../auth/abrangencia';

@Injectable()
export class OperadorasEffects {
  loadOperadoras$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperadorasActions.getOperadoras),
      switchMap(() =>
        this.store.select(UserProfileSelectors.selectUserProfile).pipe(
          skipWhile(({ idss_loaded }) => !idss_loaded),
          map(({ idss, operadorasVinculadas }) => ({ abrangencia: resolverAbrangencia(idss), operadorasVinculadas })),
          // O select nunca completa, então o effect segue inscrito no slice para sempre: sem este
          // filtro, qualquer alteração do user-profile refaz as chamadas. Ver operadoras.effects.spec.ts.
          distinctUntilChanged(
            (anterior, atual) =>
              anterior.abrangencia === atual.abrangencia &&
              anterior.operadorasVinculadas === atual.operadorasVinculadas,
          ),
          switchMap(({ abrangencia, operadorasVinculadas }) => {
            // shareReplay porque, com abrangência CLIENTES, operadorasClientes$ entra duas vezes no
            // combineLatest; sendo frio, cada assinatura dispararia uma requisição própria.
            const operadorasGeral$ = this.idssAnsGoApi.fetchOperadoras().pipe(
              map(response => response.items),
              shareReplay(1),
            );
            const operadorasClientes$ = this.idssAnsGoApi
              .fetchOperadoras({ cliente: 1 })
              .pipe(map(response => response.items), shareReplay(1));

            let operadorasWorkspace$: Observable<Operadora[] | VinculoUsuarioOperadora[]>;
            switch (abrangencia) {
              case 'CLIENTES':
                operadorasWorkspace$ = operadorasClientes$;
                break;
              case 'VINCULO':
                operadorasWorkspace$ = of(operadorasVinculadas);
                break;
              default:
                operadorasWorkspace$ = of([]);
            }
            return combineLatest([operadorasGeral$, operadorasClientes$, operadorasWorkspace$]);
          }),
          map(([operadorasGeral, operadorasClientes, operadorasWorkspace]) =>
            OperadorasActions.getOperadorasSuccess({ operadorasGeral, operadorasClientes, operadorasWorkspace }),
          ),
          catchError(() =>
            of(
              OperadorasActions.getOperadorasFailure({
                errorMsg: 'Ocorreu um erro ao buscar as operadoras na API do ORDS',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  changeSelectedOperadoraWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperadorasActions.changeSelectedOperadoraWorkspace),
      concatLatestFrom(({ cd_registro_ans }) =>
        this.store.select(OperadorasSelectors.findWorkspaceOperadoraById(cd_registro_ans)),
      ),
      switchMap(([_, operadora]) =>
        combineLatest([
          of(operadora),
          operadora ? this.simuladorGoApi.fetchParametorsOperadora(operadora.cd_registro_ans) : of(null),
          operadora ? this.geralService.getOperadoraModulosComTermino(operadora.cd_registro_ans) : of([]),
        ]).pipe(
          map(([operadora, params, modulos]) =>
            operadora
              ? OperadorasActions.changeSelectedOperadoraWorkspaceSuccess({ operadora, params, modulos })
              : OperadorasActions.changeSelectedOperadoraWorkspaceFailed(),
          ),
        ),
      ),
    ),
  );

  carregarFuncionalidadesWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperadorasActions.changeSelectedOperadoraWorkspaceSuccess),
      map(({ operadora }) => UserProfileActions.getFuncionalidades({ regans: String(operadora.cd_registro_ans) })),
    ),
  );

  changeSelectedOperadoraDadosSetor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperadorasActions.changeSelectedOperadoraDadosSetor),
      concatLatestFrom(({ cd_registro_ans }) =>
        this.store.select(OperadorasSelectors.findOperadoraById(cd_registro_ans)),
      ),
      map(([_, operadora]) => OperadorasActions.changeSelectedOperadoraDadosSetorSuccess({ operadora })),
    ),
  );

  constructor(
    private store: Store,
    private idssAnsGoApi: IdssAnsGoApi,
    private simuladorGoApi: SimuladorGoApi,
    private geralService: GeralService,
    private actions$: Actions,
  ) {}
}
